<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="trophy"/> {{ $t('pointTransactionHistory') }}</h1>
        <p>{{ $t('pointTransactionDesc') }}</p>
      </div>
    </div>

    <div class="row">
      <!-- Tabs action -->
      <div class="col-12">
        <b-tabs v-model="actionTabIndex" @input="onActionTab">
          <b-tab :title="$t('all')"></b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #28a745"></span> {{ $t('plus') }}
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #dc3545"></span> {{ $t('minus') }}
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <form-input class="mr-1 mb-1" :model="filterFormModel" attribute="keyword"
                        :placeholder="$t('enterKeyword')" v-on:keydown.enter="load"></form-input>
            <client-select v-if="!clientId" class="mr-1 mb-1" :model="filterFormModel" attribute="client"
                           :isFilter="true" :place-holder="$t('selectClient')"></client-select>
            <form-select :model="filterFormModel" attribute="type" class="mr-1 mb-1"
                         :options="[
                           {value: null, text: $t('selectType')},
                           {value: 'order', text: $t('order')},
                           {value: 'manual', text: $t('manual')},
                           {value: 'payment', text: $t('payment')},
                         ]"></form-select>
            <time-range-picker :model="filterFormModel" :label="$t('time')" class="mr-1 mb-1"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
            <point-transaction-export class="ml-auto mb-1"
                                      v-if="['owner','admin','tech','operationsManager','account'].includes($store.state.user.role)"
                                      :filter="filterFormModel" :total-count="itemCount"/>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount > 0">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <!-- Card list -->
      <div class="col-12" v-if="items.length > 0">
        <div class="log-list">
          <div v-for="item in items" :key="item._id"
               class="log-card"
               :style="{ borderLeftColor: item.action === 'plus' ? '#28a745' : '#dc3545' }">
            <div class="log-card__time">
              <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm') }}</div>
              <div class="log-meta">{{ item.createTime | moment('YYYY') }}</div>
            </div>

            <div class="log-card__type">
              <span class="log-type-pill" :style="{ background: TYPE_COLORS[item.type] || '#888' }">
                {{ $t(item.type) }}
              </span>
              <div class="log-meta mt-1" v-if="item.order">{{ $t('order') }}: {{ item.order.code }}</div>
              <div class="log-meta" v-if="item.customer">{{ item.customer.name }}</div>
            </div>

            <div class="log-card__main">
              <div class="log-row-1" v-if="item.client">
                <font-awesome-icon icon="building" class="text-muted mr-1"/>
                <strong>{{ item.client.companyName || item.client.domain }}</strong>
                <span class="log-meta ml-2" v-if="item.client.domain">{{ item.client.domain }}</span>
              </div>
              <div class="log-row-2 log-content">{{ item.content || '—' }}</div>
            </div>

            <!-- Số điểm tăng/giảm + tồn sau -->
            <div class="log-card__point">
              <div class="point-value" :class="item.action === 'plus' ? 'point-plus' : 'point-minus'">
                {{ formatPoint(item) }}
              </div>
              <div class="log-meta">→ {{ getTotalPoint(item) }}</div>
            </div>

            <div class="log-card__actions">
              <button @click.stop="del(item._id)" type="button" class="btn btn-sm btn-outline-danger is-border-1"
                      :title="$t('delete')">
                <font-awesome-icon icon="trash"/>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted py-4" v-if="items.length === 0 && !loading">
        <font-awesome-icon icon="inbox" size="3x" class="mb-2 d-block"/>
        {{ $t('notFound') }}
      </div>

      <div class="col-12" v-if="itemCount > filterFormModel.pageSize">
        <div class="c-paging clearfix">
          <div class="c-paging__count">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import List from "@/mixins/List";
import CRUD from "@/mixins/CRUD";
import TimeRangePicker from "@/components/TimeRangePicker.vue";
import ClientSelect from "@/components/ClientSelect.vue";
import PointTransactionExport from "@/views/message/export/PointTransactionExport.vue";

const TYPE_COLORS = {
  order: '#43a047',
  manual: '#1976d2',
  payment: '#8e24aa'
};

export default {
  components: { PointTransactionExport, ClientSelect, TimeRangePicker },
  mixins: [List, CRUD],
  props: { clientId: { type: String, default: null } },
  data() {
    return {
      service: '/client-point-transaction',
      autoLoad: true,
      actionTabIndex: 0,
      TYPE_COLORS,
      defaultFilterFormModel: {
        client: this.clientId || null,
        keyword: null,
        action: null,
        type: null,
      }
    }
  },
  methods: {
    onActionTab(idx) {
      this.filterFormModel.action = idx === 0 ? null : (idx === 1 ? 'plus' : 'minus');
      this.filterFormModel.page = 1;
      this.load();
    },
    formatPoint(item) {
      const point = Math.abs(item.point);
      return item.action === 'plus' ? '+' + point : '-' + point;
    },
    getTotalPoint(item) {
      const point = Math.abs(item.point);
      return item.action === 'plus' ? item.beforePoint + point : item.beforePoint - point;
    }
  },
  i18n: {
    messages: {
      en: {
        pointTransactionHistory: 'Point transaction history',
        pointTransactionDesc: 'Point earning and redemption records.',
        all: 'All',
        info: 'Info',
        point: 'Point',
        afterPoint: 'After',
        order: 'Order',
        manual: 'Manual',
        payment: 'Payment',
        plus: 'Earned',
        minus: 'Spent',
        enterKeyword: 'Search...',
        selectType: 'All types',
        selectAction: 'All actions',
        notFound: 'No records found',
      },
      vi: {
        pointTransactionHistory: 'Lịch sử tích điểm',
        pointTransactionDesc: 'Lịch sử cộng/trừ điểm khách hàng.',
        all: 'Tất cả',
        info: 'Thông tin',
        point: 'Điểm',
        afterPoint: 'Sau',
        order: 'Đơn hàng',
        manual: 'Thủ công',
        payment: 'Thanh toán',
        plus: 'Cộng',
        minus: 'Trừ',
        enterKeyword: 'Tìm...',
        selectType: 'Tất cả loại',
        selectAction: 'Tất cả hành động',
        notFound: 'Không tìm thấy bản ghi nào',
      }
    }
  }
}
</script>

<style scoped>
.log-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.log-list { display: flex; flex-direction: column; gap: 8px; }
.log-card {
  display: flex; align-items: center; background: #fff;
  border: 1px solid #e8eaed; border-left: 4px solid #888; border-radius: 6px;
  padding: 12px 16px; gap: 16px; transition: all 0.15s;
}
.log-card:hover { border-color: #c9d1d9; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.log-card__time { min-width: 100px; flex-shrink: 0; }
.log-time-main { font-size: 13px; color: #333; font-weight: 500; }
.log-meta { font-size: 11px; color: #888; }
.log-card__type { min-width: 160px; flex-shrink: 0; }
.log-type-pill {
  display: inline-block; padding: 3px 10px; border-radius: 12px;
  color: #fff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px; white-space: nowrap;
}
.log-card__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.log-row-1 { font-size: 14px; color: #222; }
.log-row-2 { font-size: 13px; color: #555; word-break: break-word; }
.log-content { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

.log-card__point {
  min-width: 100px; flex-shrink: 0; text-align: right;
}
.point-value {
  font-size: 18px; font-weight: 700; font-family: 'Menlo', monospace;
}
.point-plus { color: #28a745; }
.point-minus { color: #dc3545; }

.log-card__actions { flex-shrink: 0; }
</style>
